import React from 'react';

interface StatusBannerProps {
    status: string;
    vncUrl?: string;
    progress?: string;
    error?: string;
    hideVncLink?: boolean;
}

const statusConfig: Record<string, {
    color: string;
    bgColor: string;
    borderColor: string;
    text: string;
    icon: JSX.Element;
}> = {
    initializing: {
        color: '#666666',
        bgColor: '#F7F7F7',
        borderColor: '#E5E5E5',
        text: 'Initializing...',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12,6 12,12 16,14" />
            </svg>
        ),
    },
    generating: {
        color: '#7C3AED',
        bgColor: '#F5F3FF',
        borderColor: '#DDD6FE',
        text: 'Generating AI comments...',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
        ),
    },
    awaiting_selection: {
        color: '#10B981',
        bgColor: '#F0FDF4',
        borderColor: '#BBF7D0',
        text: 'Ready for review',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22,4 12,14.01 9,11.01" />
            </svg>
        ),
    },
    needs_login: {
        color: '#F59E0B',
        bgColor: '#FFFBEB',
        borderColor: '#FDE68A',
        text: 'Login required',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 9v2M12 15h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            </svg>
        ),
    },
    posting: {
        color: '#7C3AED',
        bgColor: '#F5F3FF',
        borderColor: '#DDD6FE',
        text: 'Posting comments...',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22,2 15,22 11,13 2,9 22,2" />
            </svg>
        ),
    },
    completed: {
        color: '#10B981',
        bgColor: '#F0FDF4',
        borderColor: '#BBF7D0',
        text: 'All done!',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22,4 12,14.01 9,11.01" />
            </svg>
        ),
    },
    error: {
        color: '#EF4444',
        bgColor: '#FEF2F2',
        borderColor: '#FECACA',
        text: 'Something went wrong',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
        ),
    },
};

const StatusBanner: React.FC<StatusBannerProps> = ({ status, vncUrl, progress, error, hideVncLink }) => {
    const config = statusConfig[status] || statusConfig.initializing;
    const isLoading = ['initializing', 'generating', 'posting'].includes(status);

    return (
        <div
            style={{
                padding: '20px 24px',
                borderRadius: '8px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '16px',
                backgroundColor: config.bgColor,
                border: `1px solid ${config.borderColor}`,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    backgroundColor: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: config.color,
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                }}>
                    {isLoading ? (
                        <div
                            style={{
                                width: '20px',
                                height: '20px',
                                border: `2px solid ${config.bgColor}`,
                                borderTop: `2px solid ${config.color}`,
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                            }}
                        />
                    ) : (
                        config.icon
                    )}
                </div>

                <div>
                    <div style={{
                        fontWeight: 600,
                        fontSize: '15px',
                        color: config.color,
                    }}>
                        {config.text}
                    </div>
                    {progress && ['initializing', 'generating'].includes(status) && (
                        <div style={{
                            fontSize: '13px',
                            color: '#666666',
                            marginTop: '4px',
                        }}>
                            {progress}
                        </div>
                    )}
                    {status === 'error' && progress && (
                        <div style={{
                            fontSize: '13px',
                            color: config.color,
                            marginTop: '4px',
                        }}>
                            {progress}
                        </div>
                    )}
                </div>
            </div>

            {vncUrl && !hideVncLink && (
                <a
                    href={vncUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        padding: '12px 20px',
                        background: status === 'needs_login' ? '#F59E0B' : '#000000',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: 600,
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.2s ease',
                    }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                        <line x1="8" y1="21" x2="16" y2="21" />
                        <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                    {status === 'needs_login' ? 'Login to Instagram' : 'View VM Screen'}
                </a>
            )}

            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style>
        </div>
    );
};

export default StatusBanner;
